/**
 * Fase 2A · P0-3 · EmpleadoOnboarding
 * Blueprint §7.1 · 3 pasos: bienvenida + foto · datos operativos · confirmación.
 * FDR #8 asumida por default · NO firma de contrato en 2A.
 */
import { useNavigate } from "react-router-dom";
import OnboardingWizard from "../../components/onboarding/OnboardingWizard";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  HardHat, User as UserIcon, CheckCircle, Sparkle,
} from "@phosphor-icons/react";

export default function EmpleadoOnboarding() {
  const navigate = useNavigate();

  const steps = [
    {
      id: "welcome",
      label: "Bienvenida",
      render: () => (
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)]">
            <HardHat weight="fill" /> Operario del centro
          </div>
          <h2 className="font-display text-2xl font-black tracking-tighter">
            Bienvenido al equipo
          </h2>
          <p className="text-sm text-[var(--ag-text-dim)]">
            AutoGlow es tu herramienta de trabajo diaria. Aquí verás la agenda del centro, los clientes,
            y podrás chatear con tu equipo y con quienes reservan contigo.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="p-3 bg-[var(--ag-midnight)]/60 border border-[var(--ag-border)] rounded text-center">
              <div className="text-xs font-bold text-[var(--ag-gold)]">Agenda</div>
              <div className="text-[11px] text-[var(--ag-text-dim)] mt-0.5">Ver y actualizar citas</div>
            </div>
            <div className="p-3 bg-[var(--ag-midnight)]/60 border border-[var(--ag-border)] rounded text-center">
              <div className="text-xs font-bold text-[var(--ag-gold)]">Clientes</div>
              <div className="text-[11px] text-[var(--ag-text-dim)] mt-0.5">Contacto y matrículas</div>
            </div>
            <div className="p-3 bg-[var(--ag-midnight)]/60 border border-[var(--ag-border)] rounded text-center">
              <div className="text-xs font-bold text-[var(--ag-gold)]">Chats</div>
              <div className="text-[11px] text-[var(--ag-text-dim)] mt-0.5">Equipo y clientes</div>
            </div>
          </div>
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-200">
            No verás datos financieros del centro. Tu titular gestiona esa parte.
          </div>
        </div>
      ),
    },
    {
      id: "profile",
      label: "Datos operativos",
      render: ({ data, updateData }) => (
        <div className="space-y-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] flex items-center gap-2">
            <UserIcon weight="fill" /> Cómo apareces al equipo
          </div>
          <h2 className="font-display text-xl font-bold">Datos personales</h2>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Nombre visible</Label>
            <Input
              value={data.name || ""}
              onChange={(e) => updateData({ name: e.target.value })}
              placeholder="Ej. Ana Operaria"
              className="mt-1"
              data-testid="empleado-onb-name"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Teléfono interno (opcional)</Label>
            <Input
              value={data.phone || ""}
              onChange={(e) => updateData({ phone: e.target.value })}
              placeholder="600 123 456"
              className="mt-1"
              data-testid="empleado-onb-phone"
            />
            <div className="text-[11px] text-[var(--ag-text-dim)] mt-1">
              Solo tu titular podrá verlo. Sirve para contactarte fuera de la app.
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">URL de tu foto (opcional)</Label>
            <Input
              type="url"
              value={data.avatar_url || ""}
              onChange={(e) => updateData({ avatar_url: e.target.value })}
              placeholder="https://…"
              className="mt-1"
              data-testid="empleado-onb-avatar"
            />
          </div>
        </div>
      ),
    },
    {
      id: "ready",
      label: "Listo",
      render: () => (
        <div className="space-y-4 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/15 text-emerald-300">
            <CheckCircle size={32} weight="fill" />
          </div>
          <h2 className="font-display text-xl font-bold">Ya está</h2>
          <p className="text-sm text-[var(--ag-text-dim)]">
            Cuando pulses <span className="text-[var(--ag-gold)]">Finalizar</span> irás a la pantalla de acceso.
            Usa el email y contraseña que te ha proporcionado tu titular.
          </p>
          <div className="inline-flex items-center gap-2 text-[11px] text-[var(--ag-text-dim)]">
            <Sparkle weight="fill" className="text-[var(--ag-gold)]" />
            Consejo: activa notificaciones para recibir nuevas citas al instante.
          </div>
        </div>
      ),
    },
  ];

  return (
    <OnboardingWizard
      roleKey="empleado"
      eyebrow="Onboarding · Empleado"
      title="Configura tu perfil operativo"
      subtitle="En 3 pasos rápidos. Podrás cambiar todo desde Mi perfil."
      steps={steps}
      homeRoute="/login"
      homeCta="Iniciar sesión"
      onComplete={() => navigate("/login")}
      onSkip={() => navigate("/login")}
    />
  );
}
